/**
 * Brief button — opt-in header / outline-row affordance that signals whether a
 * unit's brief is set and opens the BriefModal (Scoped Brief — brief-modal-spec).
 *
 * Self-sufficient: derives all three counts from the stores so it drops into
 * any surface (editor header, outline row) with no prop-threading —
 * requirements from the coverage store (links targeting this unit), themes from
 * the drafting outline's `themeIds` (a single document's from its unit brief —
 * see lib/brief-themes.ts), pinned docs from the unit-brief store.
 *
 * Three render modes:
 * - default: shows the inline `Brief · N reqs · M themes · K docs` indicator
 *   when set (unless `showCounts={false}`, used by outline rows for alignment).
 * - `compact`: icon + `Brief` only, with the count breakdown moved into a
 *   tooltip. Used in the dense editor chrome header. Same icons and tones as
 *   `status`: a document icon in foreground when set, a `+` in the subtle tone
 *   when not.
 * - `status`: the response outline table's Brief column. Always visible;
 *   a document icon + `Brief` in foreground when set (counts in the tooltip),
 *   a `+` + `Brief` in the subtle tone when not.
 */

import { useMemo } from 'react'
import { FileText, Plus } from 'lucide-react'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useUnitBriefStore } from '@/stores/unit-brief-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { themesLiveOnUnitBrief } from '@/lib/brief-themes'
import { cn } from '@/lib/utils'

interface BriefButtonProps {
  bidId: string
  artifactId: string
  sectionKey: string
  onOpen: () => void
  /**
   * Show the `· N reqs · M themes · K docs` indicator. On by default (editor
   * header). Outline rows pass `false` so every row's button is the same width
   * and stays aligned (the counts vary per row, otherwise). Ignored when
   * `compact` is set.
   */
  showCounts?: boolean
  /**
   * Compact editor-header mode: icon + `Brief` only, foreground when set, with
   * the count breakdown surfaced via a tooltip instead of inline.
   */
  compact?: boolean
  /**
   * Table-cell status mode (the response outline's Brief column): always
   * visible, set vs unset told apart by icon and tone, counts in the tooltip.
   */
  status?: boolean
}

interface CountSegment {
  value: number
  label: string
}

export function BriefButton({ bidId, artifactId, sectionKey, onOpen, showCounts = true, compact = false, status = false }: BriefButtonProps) {
  const docCount = useUnitBriefStore((s) => s.get(sectionKey)?.pinnedDocIds.length ?? 0)
  const briefThemeCount = useUnitBriefStore((s) => s.get(sectionKey)?.themeIds?.length ?? 0)
  const coverage = useCoverageStore((s) => s.coverage)
  const coverageBidId = useCoverageStore((s) => s.bidId)
  const outline = useDraftingStore((s) => s.outline)

  const themeCount = useMemo(
    () =>
      themesLiveOnUnitBrief(outline, sectionKey)
        ? briefThemeCount
        : (outline?.sections?.find((s) => s.sectionKey === sectionKey)?.themeIds?.length ?? 0),
    [outline, sectionKey, briefThemeCount],
  )

  const reqCount = useMemo(() => {
    if (!coverage || coverageBidId !== bidId) return 0
    const countLinks = (rows: typeof coverage.compliance) =>
      rows.reduce(
        (sum, row) =>
          sum +
          row.links.filter((l) => l.artifactId === artifactId && l.sectionKey === sectionKey).length,
        0,
      )
    return countLinks(coverage.compliance) + countLinks(coverage.criteria)
  }, [coverage, coverageBidId, bidId, artifactId, sectionKey])

  const segments: CountSegment[] = [
    { value: reqCount, label: reqCount === 1 ? 'req' : 'reqs' },
    { value: themeCount, label: themeCount === 1 ? 'theme' : 'themes' },
    { value: docCount, label: docCount === 1 ? 'doc' : 'docs' },
  ].filter((segment) => segment.value > 0)

  const isSet = segments.length > 0

  const countsText = segments.map((segment) => `${segment.value} ${segment.label}`).join(' · ')

  if (status) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              onClick={onOpen}
              data-brief-set={isSet ? 'true' : 'false'}
              className={cn(
                'h-7 gap-1 px-2 py-0 text-meta',
                isSet ? 'text-foreground' : 'text-subtle hover:text-foreground',
              )}
            >
              {isSet ? (
                <FileText className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <Plus className="h-3.5 w-3.5 shrink-0" />
              )}
              <span>Brief</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{isSet ? countsText : 'Add a brief for this section'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const showInlineCounts = showCounts && !compact && isSet
  const filled = compact ? isSet : showCounts && isSet

  // Compact mode reads set/unset the way the outline table's Brief column
  // does, so the header and the table never disagree about a section.
  const icon = !compact ? (
    <GradientSparkleIcon className="h-4 w-4 shrink-0" />
  ) : isSet ? (
    <FileText className="h-4 w-4 shrink-0" />
  ) : (
    <Plus className="h-4 w-4 shrink-0" />
  )
  const toneClass = compact
    ? isSet
      ? 'text-foreground'
      : 'text-subtle hover:text-foreground'
    : !filled && 'text-muted-foreground'

  const button = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onOpen}
      data-brief-set={compact ? (isSet ? 'true' : 'false') : undefined}
      className={cn('gap-1.5 font-normal', toneClass)}
    >
      {icon}
      <span>Brief</span>
      {showInlineCounts && (
        <span className="text-muted-foreground">
          {segments.map((segment) => (
            <span key={segment.label}>
              {' · '}
              {segment.value} {segment.label}
            </span>
          ))}
        </span>
      )}
    </Button>
  )

  if (!compact) return button

  const tooltipText = isSet ? countsText : 'No brief set'

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom">{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
